import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcn/table'
import { Textarea } from '@/components/shadcn/textarea'
import { cn } from '@/lib/utils'
import { IconCirclePlusOutline } from '@icons/IconCirclePlusOutline'
import { IconCloseOutline } from '@icons/IconCloseOutline'
import { useCallback, useMemo } from 'react'
import { Control, FieldPath, FieldValues } from 'react-hook-form'

type PrimitiveType = 'string' | 'number' | 'integer' | 'boolean'

export type ObjectListProperty = {
  id: string
  title: string
  type: PrimitiveType
  required?: boolean
  readOnly?: boolean
  widget?: 'input' | 'textarea'
  choices?: Array<string | number | { value: string | number; label: string }>
  placeholder?: string
}

export type ObjectListEntry = Record<string, unknown>

interface ObjectListFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>
  name: TName
  label: string
  properties: ObjectListProperty[]
  className?: string
  disabled?: boolean
  readOnly?: boolean
  emptyStateMessage?: string
}

export function ObjectListField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  properties,
  className,
  disabled = false,
  readOnly = false,
  emptyStateMessage = 'No entries yet. Click "Add row" to create one.',
}: ObjectListFieldProps<TFieldValues, TName>) {
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <ObjectListFieldInner
          value={Array.isArray(field.value) ? (field.value as ObjectListEntry[]) : []}
          onChange={field.onChange}
          label={label}
          properties={properties}
          className={className}
          disabled={disabled}
          readOnly={readOnly}
          emptyStateMessage={emptyStateMessage}
        />
      )}
    />
  )
}

type ObjectListFieldInnerProps = {
  value: ObjectListEntry[]
  onChange: (value: ObjectListEntry[]) => void
  label: string
  properties: ObjectListProperty[]
  className?: string
  disabled: boolean
  readOnly: boolean
  emptyStateMessage: string
}

function ObjectListFieldInner({
  value,
  onChange,
  label,
  properties,
  className,
  disabled,
  readOnly,
  emptyStateMessage,
}: ObjectListFieldInnerProps) {
  const entries = useMemo(() => value, [value])
  const canEdit = !disabled && !readOnly

  const createEmptyEntry = useCallback((): ObjectListEntry => {
    const entry: ObjectListEntry = {}
    for (const property of properties) {
      if (property.type === 'boolean') {
        entry[property.id] = false
        continue
      }
      entry[property.id] = ''
    }
    return entry
  }, [properties])

  const addEntry = useCallback(() => {
    onChange([...entries, createEmptyEntry()])
  }, [createEmptyEntry, entries, onChange])

  const removeEntry = useCallback(
    (index: number) => {
      const next = [...entries]
      next.splice(index, 1)
      onChange(next)
    },
    [entries, onChange]
  )

  const updateEntry = useCallback(
    (index: number, propertyId: string, nextValue: unknown) => {
      const next = entries.map((entry, i) => (i === index ? { ...entry, [propertyId]: nextValue } : entry))
      onChange(next)
    },
    [entries, onChange]
  )

  return (
    <FormItem className={cn('w-full', className)}>
      <FormLabel>{label}</FormLabel>
      <FormControl>
        <div className="flex flex-col gap-2">
          <div className="dark:bg-offwhite w-full overflow-hidden rounded-lg border border-input bg-background shadow-sm">
            <Table className="w-full table-fixed text-sm">
              <TableHeader className="[&_tr]:border-0">
                <TableRow className="!border-b border-border/80 bg-muted/60 hover:bg-muted/60">
                  {properties.map((property) => (
                    <TableHead
                      key={property.id}
                      className="h-8 px-2 py-1.5 text-center text-xs font-semibold text-foreground/70"
                    >
                      {property.title}
                    </TableHead>
                  ))}
                  {canEdit && <TableHead className="h-8 w-10 px-2 py-1.5" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell
                      colSpan={properties.length + (canEdit ? 1 : 0)}
                      className="px-2 py-3 text-center text-xs text-muted-foreground"
                    >
                      {emptyStateMessage}
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry, rowIndex) => (
                    <TableRow key={rowIndex} className="border-0 odd:bg-background even:bg-muted/40">
                      {properties.map((property) => (
                        <TableCell key={property.id} className="min-w-0 px-1 py-1 text-center align-middle">
                          <InlineCellInput
                            property={property}
                            value={entry[property.id]}
                            onChange={(nextValue) => updateEntry(rowIndex, property.id, nextValue)}
                            disabled={!canEdit || Boolean(property.readOnly)}
                          />
                        </TableCell>
                      ))}
                      {canEdit && (
                        <TableCell className="w-10 px-2 py-1 text-center align-middle">
                          <button
                            type="button"
                            className="ring-offset-offwhite rounded-full p-1 outline-none hover:bg-muted focus:ring-2 focus:ring-ring focus:ring-offset-1"
                            onClick={() => removeEntry(rowIndex)}
                          >
                            <IconCloseOutline className="size-3.5 text-muted-foreground" />
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {canEdit && (
            <div className="flex w-full justify-end">
              <Button type="button" variant="ghost" size="sm" className="h-7" onClick={addEntry}>
                <span className="mr-1 text-xs">Add row</span>
                <IconCirclePlusOutline className="size-3.5" />
              </Button>
            </div>
          )}
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  )
}

type InlineCellInputProps = {
  property: ObjectListProperty
  value: unknown
  onChange: (value: unknown) => void
  disabled: boolean
}

function InlineCellInput({ property, value, onChange, disabled }: InlineCellInputProps) {
  if (property.type === 'boolean') {
    return (
      <div className="flex h-7 items-center justify-center">
        <Checkbox
          checked={Boolean(value)}
          onCheckedChange={(checked) => onChange(Boolean(checked))}
          disabled={disabled}
        />
      </div>
    )
  }

  if (property.choices?.length) {
    return (
      <select
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        className="h-7 w-full rounded-sm border-0 bg-transparent px-2 text-xs shadow-none focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/50"
      >
        <option value="">Select...</option>
        {property.choices.map((choice) => {
          const optionValue =
            typeof choice === 'string' || typeof choice === 'number' ? String(choice) : String(choice.value)
          const optionLabel = typeof choice === 'string' || typeof choice === 'number' ? String(choice) : choice.label
          return (
            <option key={optionValue} value={optionValue}>
              {optionLabel}
            </option>
          )
        })}
      </select>
    )
  }

  if (property.widget === 'textarea') {
    return (
      <Textarea
        value={String(value ?? '')}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        placeholder={property.placeholder}
        className="min-h-12 border-0 bg-transparent px-2 py-1 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-ring/50"
      />
    )
  }

  if (property.type === 'number' || property.type === 'integer') {
    return (
      <Input
        type="number"
        value={value === '' || value === undefined || value === null ? '' : String(value)}
        onChange={(event) => {
          const parsed = event.target.valueAsNumber
          onChange(Number.isNaN(parsed) ? '' : parsed)
        }}
        disabled={disabled}
        placeholder={property.placeholder}
        className="h-7 border-0 bg-transparent px-2 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-ring/50"
      />
    )
  }

  return (
    <Input
      type="text"
      value={String(value ?? '')}
      onChange={(event) => onChange(event.target.value)}
      disabled={disabled}
      placeholder={property.placeholder}
      className="h-7 border-0 bg-transparent px-2 text-xs shadow-none focus-visible:ring-1 focus-visible:ring-ring/50"
    />
  )
}
